import { cn, useTranslation } from '@hbs-us/ui';
import { ChevronDown, ChevronRight, TriangleAlert } from 'lucide-react';
import { Fragment, useState } from 'react';
import { weekdayLabel } from './calendarDates.js';
import type { TimesheetGridRow, WeekCheck } from './contractsMirror.js';
import { EditWeekEntries } from './EditWeekEntries.js';

/**
 * The editable week grid (UI-SPEC archetype D): rows = employees, columns = the 7 days, cells =
 * hours, with a per-row running total and a per-day column total. A leading checkbox column + a
 * select-all header checkbox drive the bulk week-approval (the BulkActionBar lives in the parent
 * view). All numerals are `font-mono` (BRAND-06 numeric alignment).
 *
 * THE GUARDRAIL (D-03 / T-22-payroll): every cell shows the SERVER-COMPUTED, lunch-adjusted hours.
 * This component renders `row.days[date]` and `row.totalHours` verbatim — it performs NO lunch /
 * overtime arithmetic. Editing a cell posts the clock times to the server (via the parent's entry
 * mutation), which re-runs the preserved math; the grid then re-fetches the server result.
 *
 * Accessibility (A11Y-01): a semantic `<table>` with `scope`'d headers, an accessible name per
 * checkbox, a visible approved-week badge (TEXT, not color alone), and 44px-min controls. Every color
 * is a locked `@hbs-us/ui` token (no hex).
 *
 * RESPONSIVE (the `hidden md:block` + `md:hidden` pair the estimates/invoices/reports lists already
 * use): eleven columns is the widest grid in the product, so below `md` the table is replaced by an
 * ACCORDION rather than reflowed. A generic table→card reflow was tried first and measured 444px per
 * employee — 7220px for fifteen of them, seven phone screens to find one name. Each accordion row is
 * one line (name · week total · disclosure) and opens to that person's seven days, their week status
 * and the same Reopen action the desktop row offers. The per-day `<tfoot>` totals get the same
 * treatment as a final row so the phone loses no number the desk sees.
 *
 * Every value in both layouts is read from the SAME server field (`row.days[date]`,
 * `row.totalHours`, and the display-only `columnTotals` sum) — the accordion re-derives nothing.
 */

function fmtHours(value: number): string {
  return Number(value || 0).toFixed(2);
}

export interface WeekGridProps {
  dates: string[];
  rows: TimesheetGridRow[];
  /** The Monday this grid covers — the week the edit panel loads a person's shifts for. */
  weekStart: string;
  /** The set of selected employee ids (for bulk approval). */
  selectedIds: ReadonlySet<number>;
  onToggleRow: (employeeId: number) => void;
  onToggleAll: () => void;
  onReopenWeek: (employeeId: number) => void;
  reopeningWeek: boolean;
  /**
   * Whether the viewer holds `time.approve`. Selection checkboxes and Reopen exist
   * ONLY to drive bulk approval, so without it they are dead controls that would
   * 403 on click. Hiding them is UX; `POST /timesheets/approve-weeks` and
   * `/reopen-week` re-check the permission server-side regardless.
   */
  canApprove: boolean;
}

export function WeekGrid({
  dates,
  rows,
  weekStart,
  selectedIds,
  onToggleRow,
  onToggleAll,
  onReopenWeek,
  reopeningWeek,
  canApprove,
}: WeekGridProps) {
  const { t } = useTranslation();

  // One employee's shifts open at a time, in whichever layout is on screen. Editing is an
  // approver action, so without `time.approve` the panel is never offered (the server re-checks).
  const [editingId, setEditingId] = useState<number | null>(null);
  const toggleEdit = (employeeId: number) =>
    setEditingId((current) => (current === employeeId ? null : employeeId));

  // The full width of the desktop table: the 7 day columns plus employee, total and status,
  // plus the checkbox column when it is rendered. Must track the header exactly or the edit
  // row stops spanning the table.
  const columnCount = dates.length + (canApprove ? 4 : 3);

  // Per-day column totals (server values summed for display only — not a re-derivation of pay math).
  const columnTotals = dates.map((date) =>
    rows.reduce((sum, row) => sum + Number(row.days[date] ?? 0), 0),
  );
  const grandTotal = rows.reduce((sum, row) => sum + Number(row.totalHours ?? 0), 0);

  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.employeeId));

  // One row open at a time (phone screen); a new key replaces the old, nothing is mutated.
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const toggle = (key: string) => setExpandedKey((current) => (current === key ? null : key));

  return (
    <>
      {/* Desktop: the semantic table, unchanged. It is a plain horizontal scroller again — the
          card-reflow wrapper can never fire inside `hidden md:block`, and below `md` the accordion
          below replaces it outright. */}
      <div className="hidden overflow-x-auto rounded-md border border-border bg-card md:block">
        <table className="w-full text-left text-sm">
          <thead className="border-border border-b text-muted-foreground">
            <tr>
              {canApprove ? (
                <th scope="col" className="px-4 py-3">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={onToggleAll}
                      aria-label={t('timesheets:grid.selectAll')}
                      className="size-4 rounded border-input accent-primary"
                    />
                    <span className="sr-only">{t('timesheets:grid.selectAll')}</span>
                  </label>
                </th>
              ) : null}
              <th scope="col" className="px-4 py-3 font-medium">
                {t('timesheets:grid.employee')}
              </th>
              {dates.map((date) => (
                <th key={date} scope="col" className="px-3 py-3 text-right font-medium">
                  <span className="block">{weekdayLabel(t, date)}</span>
                  <span className="block font-mono text-muted-foreground text-xs">
                    {date.slice(5)}
                  </span>
                </th>
              ))}
              <th scope="col" className="px-4 py-3 text-right font-medium">
                {t('timesheets:grid.total')}
              </th>
              <th scope="col" className="px-4 py-3 font-medium">
                {t('timesheets:grid.weekStatus')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <Fragment key={row.employeeId}>
                <tr className="border-border border-b last:border-0 hover:bg-accent/40">
                  {canApprove ? (
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(row.employeeId)}
                        onChange={() => onToggleRow(row.employeeId)}
                        aria-label={t('timesheets:grid.selectEmployee', { name: row.employeeName })}
                        className="size-4 rounded border-input accent-primary"
                      />
                    </td>
                  ) : null}
                  <th scope="row" className="px-4 py-3 font-medium text-foreground">
                    {row.employeeName}
                  </th>
                  {dates.map((date) => (
                    <td key={date} className="px-3 py-3 text-right font-mono text-foreground">
                      {fmtHours(row.days[date] ?? 0)}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right font-mono font-semibold text-foreground">
                    {fmtHours(row.totalHours)}
                  </td>
                  <td className="px-4 py-3">
                    <WeekStatus
                      row={row}
                      onReopenWeek={onReopenWeek}
                      reopeningWeek={reopeningWeek}
                      canApprove={canApprove}
                      isEditing={editingId === row.employeeId}
                      onToggleEdit={toggleEdit}
                    />
                  </td>
                </tr>
                {editingId === row.employeeId ? (
                  <tr className="border-border border-b last:border-0">
                    <td colSpan={columnCount} className="px-4 pb-3">
                      <EditWeekEntries
                        employeeId={row.employeeId}
                        employeeName={row.employeeName}
                        weekStart={weekStart}
                        idPrefix="desk"
                      />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
          <tfoot className="border-border border-t">
            <tr>
              {/* Spacer for the checkbox column — must appear and disappear WITH it or
                  every footer total shifts one cell left of its own column. */}
              {canApprove ? <td className="px-4 py-3" /> : null}
              <th scope="row" className="px-4 py-3 font-medium text-muted-foreground">
                {t('timesheets:grid.dailyTotal')}
              </th>
              {columnTotals.map((total, index) => (
                <td
                  key={dates[index]}
                  className={cn('px-3 py-3 text-right font-mono text-muted-foreground')}
                >
                  {fmtHours(total)}
                </td>
              ))}
              <td className="px-4 py-3 text-right font-mono font-semibold text-foreground">
                {fmtHours(grandTotal)}
              </td>
              <td className="px-4 py-3" />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Phone: one compact line per employee, tap to open that person's week. */}
      <ul
        aria-label={t('timesheets:grid.weekListLabel')}
        className="grid gap-2 rounded-md border border-border bg-card p-2 md:hidden"
      >
        {canApprove ? (
          <li className="flex min-h-11 items-center gap-2 px-2">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={onToggleAll}
              aria-label={t('timesheets:grid.selectAll')}
              className="size-4 rounded border-input accent-primary"
            />
            <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
              {t('timesheets:grid.selectAll')}
            </span>
          </li>
        ) : null}

        {rows.map((row) => {
          const key = String(row.employeeId);
          const panelId = `week-grid-panel-${key}`;
          const isExpanded = expandedKey === key;
          return (
            <li key={row.employeeId} className="rounded-md border border-border bg-background">
              <div className="flex items-center">
                {/* 44px hit area around the 16px box (WCAG 2.5.8), outside the disclosure button
                    so selecting somebody never opens their week. */}
                {canApprove ? (
                  <label className="inline-flex size-11 shrink-0 cursor-pointer items-center justify-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.employeeId)}
                      onChange={() => onToggleRow(row.employeeId)}
                      aria-label={t('timesheets:grid.selectEmployee', { name: row.employeeName })}
                      className="size-4 rounded border-input accent-primary"
                    />
                  </label>
                ) : null}
                <button
                  type="button"
                  onClick={() => toggle(key)}
                  aria-expanded={isExpanded}
                  aria-controls={panelId}
                  className="flex min-h-13 flex-1 items-center gap-3 rounded-md py-2 pr-2 text-left transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="min-w-0 flex-1 truncate font-medium text-foreground text-sm">
                    {row.employeeName}
                  </span>
                  <span className="shrink-0 font-mono font-semibold text-foreground text-sm">
                    {fmtHours(row.totalHours)}
                  </span>
                  <Chevron open={isExpanded} />
                </button>
              </div>
              <div
                id={panelId}
                hidden={!isExpanded}
                className="border-border border-t px-3 py-3 pl-4"
              >
                <DayLines dates={dates} hours={dates.map((date) => row.days[date] ?? 0)} />
                <div className="mt-3 flex flex-wrap items-center gap-2 border-border border-t pt-3">
                  <span className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
                    {t('timesheets:grid.weekStatus')}
                  </span>
                  <WeekStatus
                    row={row}
                    onReopenWeek={onReopenWeek}
                    reopeningWeek={reopeningWeek}
                    canApprove={canApprove}
                    isEditing={editingId === row.employeeId}
                    onToggleEdit={toggleEdit}
                  />
                </div>
                {editingId === row.employeeId ? (
                  <EditWeekEntries
                    employeeId={row.employeeId}
                    employeeName={row.employeeName}
                    weekStart={weekStart}
                    idPrefix="phone"
                  />
                ) : null}
              </div>
            </li>
          );
        })}

        {/* The `<tfoot>` daily totals, same shape so the phone loses no number. */}
        <li className="rounded-md border border-border bg-background">
          <button
            type="button"
            onClick={() => toggle('daily-total')}
            aria-expanded={expandedKey === 'daily-total'}
            aria-controls="week-grid-panel-daily-total"
            className="flex min-h-13 w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="min-w-0 flex-1 truncate font-medium text-muted-foreground text-sm">
              {t('timesheets:grid.dailyTotal')}
            </span>
            <span className="shrink-0 font-mono font-semibold text-foreground text-sm">
              {fmtHours(grandTotal)}
            </span>
            <Chevron open={expandedKey === 'daily-total'} />
          </button>
          <div
            id="week-grid-panel-daily-total"
            hidden={expandedKey !== 'daily-total'}
            className="border-border border-t px-3 py-3"
          >
            <DayLines dates={dates} hours={columnTotals} />
          </div>
        </li>
      </ul>
    </>
  );
}

/** The disclosure affordance. Decorative — the button's name comes from its text. */
function Chevron({ open }: { open: boolean }) {
  const Icon = open ? ChevronDown : ChevronRight;
  return <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />;
}

/**
 * The seven weekday values of one week, one line each. `hours` is passed through positionally from
 * whatever the caller already holds (a row's `days` map, or the display-only column totals) — this
 * renders, it never sums.
 */
function DayLines({ dates, hours }: { dates: string[]; hours: number[] }) {
  const { t } = useTranslation();
  return (
    <dl className="grid gap-1">
      {dates.map((date, index) => (
        <div key={date} className="flex items-baseline justify-between gap-3">
          <dt className="text-muted-foreground text-sm">
            {weekdayLabel(t, date)} <span className="font-mono text-xs">{date.slice(5)}</span>
          </dt>
          <dd className="font-mono text-foreground text-sm">{fmtHours(hours[index] ?? 0)}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The week's approval state: the Approved/Pending badge (TEXT, not color alone) plus the Reopen
 * action an approved week offers. Shared verbatim by the desktop row and the phone panel so the two
 * can never drift into two different approval surfaces.
 */
function WeekStatus({
  row,
  onReopenWeek,
  reopeningWeek,
  canApprove,
  isEditing,
  onToggleEdit,
}: {
  row: TimesheetGridRow;
  onReopenWeek: (employeeId: number) => void;
  reopeningWeek: boolean;
  canApprove: boolean;
  isEditing: boolean;
  onToggleEdit: (employeeId: number) => void;
}) {
  const { t } = useTranslation();

  if (!row.isApprovedWeek) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <CrewWeekCheck check={row.weekCheck} />
        <span className="inline-flex items-center rounded-md border border-border bg-background px-2 py-1 font-medium text-muted-foreground text-xs">
          {t('timesheets:grid.pending')}
        </span>
        {/* Correcting time is an approver action, and an APPROVED week has no Edit at all:
            the server refuses it until the week is reopened, so offering it would be a lie.
            Reopen is the control that appears there instead. */}
        {canApprove ? (
          <button
            type="button"
            onClick={() => onToggleEdit(row.employeeId)}
            aria-expanded={isEditing}
            className="inline-flex min-h-9 items-center rounded-md border border-border bg-card px-2 py-1 font-medium text-foreground text-xs transition-colors hover:bg-sidebar-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {isEditing ? t('timesheets:grid.closeEdit') : t('timesheets:grid.editShifts')}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <CrewWeekCheck check={row.weekCheck} />
      <span className="inline-flex items-center rounded-md bg-secondary px-2 py-1 font-medium text-secondary-foreground text-xs">
        {t('timesheets:grid.approved')}
      </span>
      {/* The Approved/Pending badge stays visible to everyone who can read the week —
          knowing the state is not a privilege. Only the Reopen ACTION needs
          `time.approve`, which is what `POST /timesheets/reopen-week` enforces. */}
      {canApprove ? (
        <button
          type="button"
          onClick={() => onReopenWeek(row.employeeId)}
          disabled={reopeningWeek}
          className="inline-flex min-h-9 items-center rounded-md border border-border bg-card px-2 py-1 font-medium text-foreground text-xs transition-colors hover:bg-sidebar-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t('timesheets:grid.reopenWeek')}
        </button>
      ) : null}
    </div>
  );
}

/**
 * What the crew member said about this week in the phone app's Friday check. A flagged week lists
 * each day they marked and their note, so the office can fix it before approving. Shown in the
 * week-status cell of both layouts; nothing renders until the server sends a check.
 */
function CrewWeekCheck({ check }: { check?: WeekCheck }) {
  const { t } = useTranslation();
  if (!check || check.status === 'none') return null;

  if (check.status === 'confirmed') {
    return (
      <span className="basis-full text-muted-foreground text-xs">
        {t('timesheets:weekCheck.confirmed')}
      </span>
    );
  }

  return (
    <div className="basis-full rounded-md border border-warning/30 bg-warning/10 px-2 py-1.5">
      <p className="flex items-center gap-1.5 font-medium text-foreground text-xs">
        <TriangleAlert className="size-3.5 shrink-0 text-warning" aria-hidden="true" />
        {t('timesheets:weekCheck.flagged')}
      </p>
      {check.flags.length > 0 ? (
        <ul className="mt-1 grid gap-0.5">
          {check.flags.map((flag) => (
            <li key={`${flag.date}-${flag.note}`} className="text-foreground text-xs">
              <span className="font-medium">
                {weekdayLabel(t, flag.date)} <span className="font-mono">{flag.date.slice(5)}</span>
              </span>
              {flag.note ? `: ${flag.note}` : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
